/* eslint-disable no-underscore-dangle */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
// adopt
import { adopt } from 'react-adopt';
// graphQL
import gql from 'graphql-tag';
// global variables
import { defaultNumberOfRows as DEFAULT_NUMBER_OF_ROWS } from 'config';
// our components
import qs, { getQueryString } from 'helpers/queryStrings';
import RouterContext from 'helpers/routerContext';
import { Query } from 'components/common/Query';
import { PageInfo } from 'components/common/Paginator';
import ExpiredAdList from 'components/ExpiredAd/List/List';

/**
 * Query to fetch expired ads from api
 */
export const EXPIRED_AD_LIST = gql`
  query ExpiredAdList(
    $first: Int
    $after: String
    $last: Int
    $before: String
    $id: ID
  ) {
    expiredAds(
      first: $first
      after: $after
      last: $last
      before: $before
      id: $id
    ) @connection(key: "expiredAds") {
      pageInfo {
        ...PaginatorPageInfo
      }
      edges {
        node {
          id
          title
          subcategory {
            id
            title
            parentCategory {
              id
              title
            }
          }
          user {
            id
            fullname
            email
            phone
          }
          reviewed
          reviewedOn
          updatedOn
          reviewedBy {
            id
            fullname
          }
          isAuto
          matchedRules
          name
          email
          status
          lastStatus
          flagCount
          updateCount
          botScoreUpdate
          botScoreInsert
          ipInsert
          ipUpdate
          countryInsert
          countryUpdate
          province {
            name
          }
          municipality {
            name
          }
        }
      }
    }
  }
  ${PageInfo}
`;

/**
 * @class
 * @extends Component
 */
class ExpiredAdListContainer extends Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);
    const {
      numberOfRowsSelected,
      direction,
      cursor,
      id,
    } = props.initialFilters;

    this.state = {
      adId: id || '',
      numberOfRowsSelected:
        parseInt(numberOfRowsSelected, 0) || DEFAULT_NUMBER_OF_ROWS,
      pagination: { direction: direction || null, cursor: cursor || undefined },
    };

    this.Compose = adopt({
      query: this.listExpiredAds,
    });

    this.fetchMore = () => {};
  }

  /**
   * @description
   * Query callback to compose with mutations.
   *
   * @param {*} render - Query's render props.
   * @returns {*} Query component.
   */
  listExpiredAds = ({ render }) => {
    const { adId, pagination, numberOfRowsSelected } = this.state;
    const paginationVariables =
      !pagination.direction || pagination.direction === 'NEXT'
        ? {
            first: numberOfRowsSelected,
            after: pagination.cursor,
          }
        : {
            last: numberOfRowsSelected,
            before: pagination.cursor,
          };
    return (
      <Query
        query={EXPIRED_AD_LIST}
        variables={{
          id: adId,
          ...paginationVariables,
        }}
        notifyOnNetworkStatusChange
        fetchPolicy="cache-and-network"
      >
        {({ data, loading, fetchMore }) => {
          this.fetchMore = fetchMore;
          return render({ data, loading, fetchMore });
        }}
      </Query>
    );
  };

  /**
   * @description
   * Filter by id.
   *
   * @param {string} id - Id for filter apply.
   */
  handleFilterById = id => {
    const history = this.context;

    this.setState({
      adId: id,
      pagination: { direction: null, cursor: undefined },
    });

    const currentLocation = history.location.pathname;
    const { numberOfRowsSelected } = getQueryString(history);

    const urlString = qs.stringify({
      id,
      numberOfRowsSelected,
      direction: undefined,
      cursor: undefined,
    });
    history.push({ pathname: currentLocation, search: urlString });
  };

  /**
   * @description
   * Change number of rows selected.
   *
   * @param {number} selectedItem - New number of rows selected.
   */
  handleChangeNumberOfRows = selectedItem => {
    const history = this.context;
    this.setState({
      numberOfRowsSelected: selectedItem,
      pagination: { direction: null, cursor: undefined },
    });
    const currentLocation = history.location.pathname;
    const parsed = getQueryString(history);
    const urlString = qs.stringify({
      ...parsed,
      direction: undefined,
      cursor: undefined,
    });
    history.push({ pathname: currentLocation, search: urlString });
  };

  /**
   * @description
   * Fetch more rows when user change page.
   *
   * @param {Object} paginatorVariables - Pagination query's variables.
   */
  handleChangePage = ({ direction, cursor }) => {
    const history = this.context;
    this.setState({ pagination: { direction, cursor } });
    const currentLocation = history.location.pathname;
    const parsed = getQueryString(history);
    const stringified = qs.stringify({
      ...parsed,
      direction,
      cursor,
    });
    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Refresh the list.
   * @returns {Function} - Fetch More function.
   */
  handleRefresh = () => {
    this.fetchMore({
      updateQuery: (_, { fetchMoreResult }) => fetchMoreResult,
    });
  };

  /**
   * @description
   * Render AdsList component.
   */
  render() {
    const { numberOfRowsSelected, adId, pagination } = this.state;
    return (
      <this.Compose>
        {({ query: { loading, data } }) => (
          <ExpiredAdList
            data={data}
            numberOfRowsSelected={numberOfRowsSelected}
            paginationDirection={pagination.direction}
            onChangePage={this.handleChangePage}
            onRefresh={this.handleRefresh}
            onChangeNumberOfRows={this.handleChangeNumberOfRows}
            adId={adId}
            onFilterById={this.handleFilterById}
            loading={loading || Object.keys(data).length === 0}
            fullLoading={loading}
          />
        )}
      </this.Compose>
    );
  }
}

/**
 * ExpiredAdListContainer PropTypes
 * @param t - prop from HOC with string translations
 * @param initialFilters - prop initial Filters to apply
 */

ExpiredAdListContainer.propTypes = {
  initialFilters: PropTypes.shape({}).isRequired,
};

export default withTranslation()(ExpiredAdListContainer);
